/**
 * Wires URL state and four generated hooks into `ReviveSheet`'s props, runs
 * the mutation, and makes D6 actually true.
 *
 * `useEntryRevive`'s generated `invalidateRelated` is a literal no-op
 * (`/entry/revive` ships `reactQueryDependencies: []`), so this container
 * invalidates the three affected reads by hand on a successful revive
 * (`plan.md` `## 0.3`).
 *
 * The eight `CONFLICT` reasons are never rendered as raw text: `conflictTreatment`
 * (`./reviveSheetModel`) maps each of the four pre-flightable ones to an inline
 * note and everything else, including an unrecognized future reason, to the
 * generic modal via `showApiError` (`plan.md` `## 0.7`, `## 0.8`).
 */
import {
  useContestGet,
  useEntryListForMember,
  useEntryRevive,
  useRoundList,
} from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type JSX, useEffect, useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import { useTeamNames } from "../pick/useTeamNames";
import { ReviveSheet } from "./ReviveSheet";
import {
  buildRevivePayload,
  conflictTreatment,
  DEFAULT_LIVES_GRANTED,
  maxLivesGranted,
  releasableTeamIds,
  resolveReviveBlock,
} from "./reviveSheetModel";

export const REVIVE_SHEET_NAME = "revive-entry";

export type typeReviveSheetContainerProps = {
  poolId: string;
  memberUserId: string;
  memberName: string;
  open: boolean;
  onClose: () => void;
};

export function ReviveSheetContainer(
  props: typeReviveSheetContainerProps,
): JSX.Element {
  const { poolId, memberUserId, memberName, open, onClose } = props;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const urlEntryId =
    typeof search.entryId === "string" && search.entryId !== ""
      ? search.entryId
      : null;

  const entryList = useEntryListForMember(
    { poolId, targetUserId: memberUserId, limit: 100 },
    { enabled: open },
  );
  const eliminatedEntries = (entryList.data?.data?.items ?? []).filter(
    (item) => item.status === "eliminated",
  );
  const entryOptions = eliminatedEntries.map((entry) => ({
    entryId: entry.entryId,
    label: entry.label,
  }));

  const selected =
    (urlEntryId !== null
      ? eliminatedEntries.find((entry) => entry.entryId === urlEntryId)
      : null) ??
    (eliminatedEntries.length === 1 ? (eliminatedEntries[0] ?? null) : null);

  const contest = useContestGet(
    { poolId, contestId: selected?.contestId ?? "" },
    { enabled: selected !== null },
  );
  const rounds = useRoundList(
    { poolId, contestId: selected?.contestId ?? "", limit: 100 },
    { enabled: selected !== null },
  );

  const eliminatedAtRoundId = selected?.eliminatedAtRoundId;
  const roundLabel =
    eliminatedAtRoundId == null
      ? null
      : (rounds.data?.data?.items.find(
          (round) => round.roundId === eliminatedAtRoundId,
        )?.label ?? null);

  const contestDefault =
    contest.data?.data?.config.requiredPicksPerRound ?? null;
  const selectedEntryId = selected?.entryId ?? null;
  const [pickRequirement, setPickRequirement] = useState<number>(1);
  const syncedForEntryIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (contestDefault === null) return;
    if (syncedForEntryIdRef.current === selectedEntryId) return;
    syncedForEntryIdRef.current = selectedEntryId;
    setPickRequirement(contestDefault);
  }, [contestDefault, selectedEntryId]);

  const [inlineNote, setInlineNote] = useState<string | null>(null);
  // ISSUE-1326. Lives and released teams belong to the entry being revived,
  // so picking another entry starts both over at the legacy default.
  const [livesGranted, setLivesGranted] = useState<number>(
    DEFAULT_LIVES_GRANTED,
  );
  const [releaseTeamIds, setReleaseTeamIds] = useState<string[]>([]);
  useEffect(() => {
    setInlineNote(null);
    setLivesGranted(DEFAULT_LIVES_GRANTED);
    setReleaseTeamIds([]);
  }, [selectedEntryId]);
  const teamNames = useTeamNames({ poolId });
  const releasableTeams = releasableTeamIds(selected?.usedTeamIds).map(
    (teamId) => ({
      teamId,
      name: teamNames.teamsById.get(teamId)?.name ?? teamId,
    }),
  );
  const maxLives = maxLivesGranted(contest.data?.data?.config.livesAllowed);

  const block = resolveReviveBlock({
    name: memberName,
    entryStatus: selected?.status ?? null,
    contestStatus: contest.data?.data?.status ?? null,
    revivesAllowed: contest.data?.data?.config.revivesAllowed ?? null,
    revivesAllowedCount: contest.data?.data?.config.revivesAllowedCount,
    usedThisContest: selected?.revivesUsedThisContest ?? 0,
  });

  const revive = useEntryRevive();
  const isSubmitting = revive.isPending;
  const isLoading =
    open &&
    (entryList.isLoading ||
      contest.isLoading ||
      rounds.isLoading ||
      teamNames.isLoading);

  function handleSelectEntry(entryId: string): void {
    void navigate({
      to: ".",
      search: (previous: unknown) => ({
        ...(previous as Record<string, unknown>),
        entryId,
      }),
    });
  }

  async function handleConfirm(): Promise<void> {
    if (selected === null || contestDefault === null) return;

    const payload = buildRevivePayload({
      poolId,
      entryId: selected.entryId,
      pickRequirement,
      contestRequiredPicksPerRound: contestDefault,
      livesGranted,
      teamsToRelease: releaseTeamIds,
    });
    const result = await revive.mutateAsync(payload);

    if (result.error) {
      const reason =
        typeof result.error.details?.reason === "string"
          ? result.error.details.reason
          : null;
      const treatment = conflictTreatment({
        reason,
        name: memberName,
        allowedCount: contest.data?.data?.config.revivesAllowedCount,
      });
      if (treatment.kind === "inline") {
        setInlineNote(treatment.note);
        await entryList.refetch();
        return;
      }
      showApiError(result);
      await entryList.refetch();
      onClose();
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: ["post:entry:entryListForMember"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["post:member:memberList"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["post:contest:contestGet"],
    });
    onClose();
  }

  return (
    <ReviveSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      name={memberName}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      entryOptions={entryOptions}
      selectedEntryId={selected?.entryId ?? null}
      onSelectEntry={handleSelectEntry}
      roundLabel={roundLabel}
      usedThisContest={selected?.revivesUsedThisContest ?? 0}
      usedThisSeason={selected?.revivesUsedThisSeason ?? 0}
      revivesAllowed={contest.data?.data?.config.revivesAllowed ?? null}
      revivesAllowedCount={contest.data?.data?.config.revivesAllowedCount}
      block={block}
      inlineNote={inlineNote}
      pickRequirement={pickRequirement}
      onPickRequirementChange={setPickRequirement}
      livesGranted={livesGranted}
      maxLives={maxLives}
      onLivesGrantedChange={setLivesGranted}
      releasableTeams={releasableTeams}
      releaseTeamIds={releaseTeamIds}
      onReleaseTeamIdsChange={setReleaseTeamIds}
      onConfirm={() => {
        void handleConfirm();
      }}
    />
  );
}
